import { useRef, useEffect, useState } from "react";
import { useLandingSeasonHero } from "@/hooks/useLandingSeasonHero";
import { cn } from "@/lib/utils";

interface ExploreHeroProps {
  title: string;
  subtitle: string;
  /** Compact variant for mobile (no image, just text) */
  compact?: boolean;
}

/**
 * ExploreHero v2 — magnetic, editorial entry point for Explore.
 *
 * Desktop: Full-width hero with seasonal background image from media_assets,
 * gradient overlay, and bold editorial title. Feels like a destination.
 *
 * Mobile compact: Lightweight text-only hero (used in mobile list view).
 *
 * Falls back gracefully: seasonal image → soft gradient.
 */
export function ExploreHero({ title, subtitle, compact = false }: ExploreHeroProps) {
  const [displayTitle, setDisplayTitle] = useState(title);
  const [opacity, setOpacity] = useState(1);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const { imageUrl, isLoading } = useLandingSeasonHero();

  useEffect(() => {
    if (title === displayTitle) return;
    setOpacity(0);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setDisplayTitle(title);
      setOpacity(1);
    }, 200);
    return () => clearTimeout(timeoutRef.current);
  }, [title]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mobile compact: minimal text hero
  if (compact) {
    return (
      <div className="px-4 pt-2 pb-1" style={{ minHeight: 52 }}>
        <h1
          className="text-lg font-semibold text-foreground leading-tight"
          style={{ opacity, transition: "opacity 200ms ease-in-out" }}
        >
          {displayTitle}
        </h1>
        <p className="text-sm text-muted-foreground mt-0.5 line-clamp-1">{subtitle}</p>
      </div>
    );
  }

  const hasImage = !!imageUrl && !isLoading;

  return (
    <div className="relative overflow-hidden rounded-2xl">
      {/* Background */}
      {hasImage ? (
        <div className="absolute inset-0">
          <img
            src={imageUrl}
            alt=""
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10" />
        </div>
      ) : (
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(160deg, hsl(var(--secondary)) 0%, hsl(var(--muted)) 50%, hsl(var(--accent)) 100%)",
          }}
        />
      )}

      {/* Content */}
      <div
        className={cn(
          "relative z-10 px-8 pb-8",
          hasImage ? "pt-20 md:pt-28" : "pt-12 md:pt-16",
        )}
      >
        <h1
          className={cn(
            "font-display text-3xl md:text-4xl lg:text-5xl font-bold leading-tight tracking-tight max-w-lg",
            hasImage ? "text-white" : "text-foreground",
          )}
          style={{ opacity, transition: "opacity 200ms ease-in-out" }}
        >
          {displayTitle}
        </h1>
        <p
          className={cn(
            "text-base md:text-lg mt-2 max-w-md leading-relaxed",
            hasImage ? "text-white/70" : "text-muted-foreground",
          )}
        >
          {subtitle}
        </p>
      </div>
    </div>
  );
}
